import { CommonModule, DatePipe } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ProjectService } from 'src/app/Services/project.service';
import { DummyDataService } from 'src/app/Services/dummy-data.service';

export interface AbsenceCalendarDialogData {
  startDate: string;
  endDate: string;
  teamId: number | null;
  shiftId: number | null;
  rangeLabel: string;
}

export interface AbsenceCalendarDialogResult {
  openUserWise?: boolean;
  user_id?: number;
  name?: string;
}

@Component({
  selector: 'app-absence-calendar-dialog',
  templateUrl: './absence-calendar-dialog.component.html',
  styleUrl: './absence-calendar-dialog.component.css',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatIconModule, NgxSkeletonLoaderModule],
  providers: [DatePipe],
})
export class AbsenceCalendarDialogComponent implements OnInit {
  readonly hint =
    'Absent = zero logs on a scheduled working day. Off-shift logs (e.g. weekend) show separately.';

  loading = true;
  absenceDays: any[] = [];
  absenceSummary: any = null;
  selectedAbsenceDetail: any = null;
  absenceDayExpanded: Record<string, boolean> = {};
  readonly absencePreviewLimit = 4;

  constructor(
    private dialogRef: MatDialogRef<AbsenceCalendarDialogComponent, AbsenceCalendarDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: AbsenceCalendarDialogData,
    private projectService: ProjectService,
    private _dummyDataService: DummyDataService,
    public datePipe: DatePipe
  ) {}

  ngOnInit(): void {
    this.load();
  }

  async load(): Promise<void> {
    const { startDate, endDate, teamId, shiftId } = this.data;
    if (!startDate || !endDate) {
      this.loading = false;
      return;
    }
    this.loading = true;
    this.projectService.getTimesheetPeriodAbsenceCalendar(startDate, endDate, teamId, shiftId).subscribe({
      next: async (res: any) => {
        const checkDemo = await this._dummyDataService.checkIsAvailableDemoData<any>('AbsenceCalendar');
        if (checkDemo.status) {
          const d = checkDemo.data || {};
          this.absenceDays = Array.isArray(d.days) ? d.days : [];
          this.absenceSummary = d.summary || null;
          this.absenceDayExpanded = {};
          this.loading = false;
          return;
        }
        const d = res?.data || {};
        this.absenceDays = Array.isArray(d.days) ? d.days : [];
        this.absenceSummary = d.summary || null;
        this.absenceDayExpanded = {};
        this.loading = false;
      },
      error: async () => {
        const checkDemo = await this._dummyDataService.checkIsAvailableDemoData<any>('AbsenceCalendar');
        if (checkDemo.status) {
          const d = checkDemo.data || {};
          this.absenceDays = Array.isArray(d.days) ? d.days : [];
          this.absenceSummary = d.summary || null;
          this.absenceDayExpanded = {};
          this.loading = false;
          return;
        }
        this.absenceDays = [];
        this.absenceSummary = null;
        this.loading = false;
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  selectAbsencePill(day: any, employee: any): void {
    this.selectedAbsenceDetail = {
      date: day?.date || '',
      ...employee,
    };
  }

  absenceEmployeesForDisplay(day: any): any[] {
    const list = Array.isArray(day?.absent_employees) ? day.absent_employees : [];
    const key = day?.date as string;
    if (!key || list.length <= this.absencePreviewLimit) {
      return list;
    }
    if (this.absenceDayExpanded[key]) {
      return list;
    }
    return list.slice(0, this.absencePreviewLimit);
  }

  absenceHiddenCount(day: any): number {
    const list = Array.isArray(day?.absent_employees) ? day.absent_employees : [];
    const n = list.length - this.absencePreviewLimit;
    return n > 0 ? n : 0;
  }

  absenceScheduledCount(day: any): number | null {
    const v = day?.scheduled_employees;
    if (v === undefined || v === null) {
      return null;
    }
    const n = Number(v);
    return isNaN(n) ? null : n;
  }

  isAbsenceAllPresentDay(day: any): boolean {
    if (day?.absent_count) {
      return false;
    }
    const s = this.absenceScheduledCount(day);
    if (s === null) {
      return true;
    }
    return s > 0;
  }

  isAbsenceNoScheduledWorkDay(day: any): boolean {
    if (day?.absent_count) {
      return false;
    }
    if (this.offScheduleCount(day) > 0) {
      return false;
    }
    return this.absenceScheduledCount(day) === 0;
  }

  offScheduleCount(day: any): number {
    const list = day?.off_schedule_employees;
    return Array.isArray(list) ? list.length : 0;
  }

  selectOffSchedulePill(day: any, row: any): void {
    this.selectedAbsenceDetail = {
      date: day?.date || '',
      user_id: row?.user_id,
      name: row?.name,
      shift_name: row?.shift_name || '—',
      shift_start_time: '—',
      shift_end_time: '—',
      reason: row?.note || 'Activity on non-working day',
    };
  }

  isAbsenceDayExpanded(day: any): boolean {
    const key = day?.date as string;
    return !!(key && this.absenceDayExpanded[key]);
  }

  toggleAbsenceDayExpand(day: any, event?: Event): void {
    event?.stopPropagation();
    const key = day?.date as string;
    if (!key) {
      return;
    }
    this.absenceDayExpanded = {
      ...this.absenceDayExpanded,
      [key]: !this.absenceDayExpanded[key],
    };
  }

  openUserWiseFromDetail(): void {
    const userId = this.selectedAbsenceDetail?.user_id;
    if (!userId) {
      return;
    }
    this.dialogRef.close({
      openUserWise: true,
      user_id: userId,
      name: this.selectedAbsenceDetail?.name || 'Employee',
    });
  }
}
